import React, { useEffect, useMemo, useState } from 'react';
import { createTelegramAdapter, TelegramAdapter } from './telegramAdapter.js';
import { TelegramContext } from './telegramContext.js';
import { applyTelegramTheme } from './telegramTheme.js';
import type { TelegramEnvironmentState } from './telegramTypes.js';

function readEnvironment(adapter: TelegramAdapter): TelegramEnvironmentState {
  return {
    runtime: adapter.getRuntime(),
    isTelegram: adapter.isTelegram(),
    colorScheme: adapter.getColorScheme(),
    themeParams: adapter.getThemeParams(),
    safeArea: adapter.getSafeArea(),
    contentSafeArea: adapter.getContentSafeArea(),
    viewportHeight: adapter.getViewportHeight(),
  };
}

interface TelegramProviderProps {
  readonly children: React.ReactNode;
  readonly adapter?: TelegramAdapter;
}

export const TelegramProvider: React.FC<TelegramProviderProps> = ({ children, adapter }) => {
  const resolvedAdapter = useMemo(() => adapter ?? createTelegramAdapter(), [adapter]);
  const [environment, setEnvironment] = useState(() => readEnvironment(resolvedAdapter));

  useEffect(() => {
    const refresh = (): void => setEnvironment(readEnvironment(resolvedAdapter));
    const unsubscribe = [
      resolvedAdapter.subscribe('themeChanged', refresh),
      resolvedAdapter.subscribe('viewportChanged', refresh),
      resolvedAdapter.subscribe('safeAreaChanged', refresh),
      resolvedAdapter.subscribe('contentSafeAreaChanged', refresh),
    ];

    if (resolvedAdapter.isTelegram()) {
      resolvedAdapter.ready();
      resolvedAdapter.expand();
    }

    return () => unsubscribe.forEach((dispose) => dispose());
  }, [resolvedAdapter]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.runtime = environment.runtime;
    root.dataset.theme = environment.colorScheme;
    applyTelegramTheme(
      root,
      environment.themeParams,
      environment.safeArea,
      environment.contentSafeArea
    );
  }, [environment]);

  const value = useMemo(
    () => ({ adapter: resolvedAdapter, environment }),
    [resolvedAdapter, environment]
  );
  return <TelegramContext.Provider value={value}>{children}</TelegramContext.Provider>;
};
